import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check } from 'lucide-react'
import { adminApi } from '@/api/admin'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

/**
 * My Company > Live: the organisation's own switch for CertSlate Live. It is on for everyone by default; turning it off
 * hides Live everywhere and stops new sessions, and turning it back on restores it all (nothing is ever deleted).
 * It saves as soon as it is changed, since there is nothing else on the page to fill in.
 */
export default function LiveSettingsCard() {
  const queryClient = useQueryClient()
  const { data: profile } = useQuery({
    queryKey: ['company-profile'],
    queryFn: () => adminApi.getCompanyProfile().then(r => r.data),
  })
  const [saved, setSaved] = useState(false)

  const mutation = useMutation({
    mutationFn: (enabled: boolean) => adminApi.updateLiveSettings(enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-profile'] })
      queryClient.invalidateQueries({ queryKey: ['live-status'] })
      queryClient.invalidateQueries({ queryKey: ['live-activities'] })
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    },
  })

  const enabled = profile?.liveDeliveryEnabled ?? true

  const change = (next: boolean) => {
    if (!next && !confirm(
      'Turn off CertSlate Live?\n\nThe Live activities tab, the Live cards on bookings and sessions, and the Live results will be hidden, and nobody can start a live session. Nothing is deleted: turn it back on to get it all back. Attendance sign-in is not affected.',
    )) return
    mutation.mutate(next)
  }

  return (
    <Card className="max-w-2xl">
      <CardHeader><CardTitle className="text-base">CertSlate Live</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-gray-500">
          Run live quizzes, polls, word clouds and Q&amp;A with your attendees on their phones, from any booking or public session,
          and see the results afterwards. Set up what trainers can run on each course&apos;s Live activities tab.
        </p>

        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={enabled}
            disabled={!profile || mutation.isPending}
            onChange={e => change(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          <span>
            <span className="block text-sm font-medium text-gray-900">Enable CertSlate Live</span>
            <span className="block text-xs text-gray-500">
              On by default. Turning it off hides Live activities and results everywhere and stops new live sessions from starting.
              Your activities and past results are kept, and come back when you turn it on again.
            </span>
          </span>
        </label>

        <p className="text-xs text-gray-400">
          Attendance sign-in (attendees scanning a QR code to sign in to a register) is separate and always available.
        </p>

        {saved && <p className="flex items-center gap-1.5 text-sm text-green-700"><Check size={14} /> Saved</p>}
        {mutation.isError && <p className="text-sm text-red-600">Couldn&apos;t save that. Please try again.</p>}
      </CardContent>
    </Card>
  )
}
